// src/components/LoginModal/index.tsx
import React, { ChangeEvent, useContext, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ToastContainer } from "react-toastify";
import { toastIt, validateEmail, verifyPasswordFormat } from "@utils";
import { BackdropContext, GlobalContext } from "@contexts";
import { handleApiLogin, handleApiRegister } from "@api";

interface ModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

interface ILoginFields {
  email: string;
  password: string;
  confirmPassword: string;
}

export const LoginModal: React.FC<ModalProps> = ({ open, setOpen }) => {
  const [selectedOption, setSelectedOption] = useState<"login" | "register">(
    "login"
  );
  const { setBackdropIsOpen } = useContext(BackdropContext);
  const { setToken } = useContext(GlobalContext);

  const [formFields, setFormFields] = useState<ILoginFields>({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [registerFormFields, setRegisterFormFields] = useState<ILoginFields>({
    email: "",
    password: "",
    confirmPassword: "",
  });

  const handleClose = () => setOpen(false);

  const handleFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegisterFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setRegisterFormFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogin = async () => {
    const { email, password } = formFields;
    if (!validateEmail(email)) {
      toastIt("Wrong email format, please try again.", 2);
      return;
    }
    if (!verifyPasswordFormat(password)) {
      toastIt(
        "Wrong password format,passwords must contain at least 8 characters, 1 uppercase letter,1 lowercase letter, and a number.",
        2
      );
      return;
    }

    setBackdropIsOpen(true);
    const loginResponse = await handleApiLogin(email, password);
    setBackdropIsOpen(false);

    if (!loginResponse.data && loginResponse.message) {
      toastIt(loginResponse.message, 2);
      return;
    }
    if (loginResponse.message && loginResponse.data) {
      setToken(loginResponse.data);
      setOpen(false);
    }
  };

  const handleRegister = async () => {
    const { email, password, confirmPassword } = registerFormFields;
    if (!validateEmail(email)) {
      toastIt("Wrong email format, please try again.", 2);
      return;
    }
    if (!verifyPasswordFormat(password)) {
      toastIt(
        "Wrong password format,passwords must contain at least 8 characters, 1 uppercase letter,1 lowercase letter, and a number.",
        2
      );
      return;
    }
    if (password !== confirmPassword) {
      toastIt("Passwords don't match, please try again.", 2);
      return;
    }

    try {
      setBackdropIsOpen(true);
      const result = await handleApiRegister(email, password, confirmPassword);
      setBackdropIsOpen(false);
      if (result.message) {
        toastIt(result.message, 1);
        setSelectedOption("login");
      }
    } catch (error) {
      setBackdropIsOpen(false);
      toastIt("Email already exists.", 2);
      console.error("Error:", error);
    }
  };

  return (
    <Box>
      <Modal
        open={open}
        onClose={handleClose}
        keepMounted
        slotProps={{
          backdrop: {
            sx: { backgroundColor: "rgba(0,0,0,0.6)" },
          },
        }}
      >
        {/* Contenedor centrado */}
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          {/* “Paper” */}
          <Box
            sx={{
              width: "100%",
              maxWidth: 980,
              bgcolor: "background.paper",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 24,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {/* Sección izquierda */}
            <Box
              sx={{
                position: "relative",
                flex: 1,
                minHeight: { xs: 160, md: "auto" },
                p: { xs: 2, md: 3 },
                color: "common.white",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 1.5,
                background:
                  "linear-gradient(135deg, rgba(0,0,0,0.6), rgba(60,60,60,0.6)), url(/hero.jpg)",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {/* Cerrar (mobile) */}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  color: "common.white",
                  display: { xs: "inline-flex", md: "none" },
                }}
                aria-label="close"
                size="large"
              >
                <CloseIcon />
              </IconButton>

              <Typography
                variant="h4"
                sx={{ fontWeight: 800, letterSpacing: 0.5 }}
              >
                LOREM IPSUM
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                Many desktop publishing packages and web page editors now use
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button
                  onClick={() => setSelectedOption("login")}
                  variant={
                    selectedOption === "login" ? "contained" : "outlined"
                  }
                  color="primary"
                  sx={{ flex: 1, borderRadius: 10 }}
                >
                  Login
                </Button>
                <Button
                  onClick={() => setSelectedOption("register")}
                  variant={
                    selectedOption === "register" ? "contained" : "outlined"
                  }
                  color="primary"
                  sx={{ flex: 1, borderRadius: 10 }}
                >
                  Register
                </Button>
              </Box>
            </Box>

            {/* Sección derecha */}
            <Box
              sx={{
                flex: 1,
                p: { xs: 3, md: 4 },
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {/* Cerrar (desktop) */}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton
                  onClick={handleClose}
                  sx={{ display: { xs: "none", md: "inline-flex" } }}
                  aria-label="close"
                  size="large"
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Typography
                variant="h5"
                sx={{ textAlign: "center", fontWeight: 700, mb: 1 }}
              >
                LOGO
              </Typography>

              {/* Opcional: separadores de login social */}
              {/* <Box sx={{ display: "grid", gap: 1 }}>
                <Button variant="outlined" startIcon={<SomeIcon />}>Ingresar con ...</Button>
                <Button variant="outlined" startIcon={<SomeIcon />}>Ingresar con ...</Button>
              </Box>
              <Divider>o</Divider> */}

              {selectedOption === "login" ? (
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleLogin();
                  }}
                  sx={{ display: "grid", gap: 2 }}
                >
                  <TextField
                    name="email"
                    variant="filled"
                    label="Email"
                    type="email"
                    fullWidth
                    value={formFields.email}
                    onChange={handleFieldChange}
                  />
                  <TextField
                    name="password"
                    variant="filled"
                    label="Password"
                    type="password"
                    fullWidth
                    value={formFields.password}
                    onChange={handleFieldChange}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    sx={{ py: 1.2 }}
                  >
                    Login
                  </Button>
                </Box>
              ) : (
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleRegister();
                  }}
                  sx={{ display: "grid", gap: 2 }}
                >
                  <TextField
                    name="email"
                    variant="filled"
                    label="Email"
                    type="email"
                    fullWidth
                    value={registerFormFields.email}
                    onChange={handleRegisterFieldChange}
                  />
                  <TextField
                    name="password"
                    variant="filled"
                    label="Password"
                    type="password"
                    fullWidth
                    value={registerFormFields.password}
                    onChange={handleRegisterFieldChange}
                  />
                  <TextField
                    name="confirmPassword"
                    variant="filled"
                    label="Confirm password"
                    type="password"
                    fullWidth
                    value={registerFormFields.confirmPassword}
                    onChange={handleRegisterFieldChange}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    sx={{ py: 1.2 }}
                  >
                    Register
                  </Button>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />
              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "text.secondary" }}
              >
                ¿desktop publishing packages?
              </Typography>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Toasts */}
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
};
